'use client';

import { Calendar, Clock, MapPin, Search, Tag, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { DateRangeFilter, MultiSelectFilterCompact } from '@ui/components/features/search/filterInputs';
import { type CategoryItem } from '@ui/components/features/search/searchMobileHeader';
import { Popover, PopoverAnchor, PopoverContent } from '@ui/components/ui/popover';
import { cn } from '@ui/lib/utils';
import {
  KeywordSuggestionsList,
  MAX_KEYWORD_QUERY_LENGTH,
  MIN_KEYWORD_QUERY_LENGTH,
  type SuggestionItem,
  buildSuggestionItems,
  suggestionOptionId,
  useSuggestionNavigation
} from '@website/components/KeywordSuggestionsDropdown';
import { SEARCH_PARAMS } from '@website/constants/searchParams';
import { useIsMobile } from '@website/hooks/useIsMobile';
import { useSearchParam } from '@website/hooks/useSearchParam';
import { useSuggestions } from '@website/hooks/useSuggestions';
import { useSyncSearchParams } from '@website/hooks/useSyncSearchParams';

import { LocationFilter } from './LocationFilter';

interface SearchFiltersProps {
  className?: string;
  categories?: CategoryItem[];
  setLocation: (location: { latitude: number; longitude: number; name: string }) => void;
  onLocationPickerOpenChange?: (open: boolean) => void;
}

export function SearchFilters({ className, categories, setLocation, onLocationPickerOpenChange }: SearchFiltersProps) {
  const isMobile = useIsMobile();
  const t = useTranslations();
  const tCommon = useTranslations('Common');
  const locale = useLocale();
  const router = useRouter();

  // The keyword is free text: read/write the URL param raw. Routing it through
  // useSearchParam's comma-separated multi-value machinery would corrupt
  // queries containing commas and diverge from what the API receives.
  const { get: getParam, replaceAll: replaceParams } = useSyncSearchParams();
  const currentQuery = getParam(SEARCH_PARAMS.QUERY) || '';
  const [localQuery, setLocalQuery] = useState(currentQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(currentQuery);
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  // Mobile: the keyword input collapses to an icon-only chip and expands as an
  // overlay over the filter row — the bar is already dense and must not grow
  // vertically.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const inlineKeywordInputRef = useRef<HTMLInputElement>(null);
  const mobileChipRef = useRef<HTMLButtonElement>(null);
  const mobileOverlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Sync the input from the URL (back/forward, external updates) — but never
    // while the user is typing in it: the URL write is debounced + trimmed, so
    // syncing mid-typing would clobber a trailing space the user just typed.
    const active = document.activeElement;
    if (active === keywordInputRef.current || active === inlineKeywordInputRef.current) return;
    setLocalQuery(currentQuery);
  }, [currentQuery]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(localQuery), 200);
    return () => clearTimeout(id);
  }, [localQuery]);

  useEffect(() => {
    if (mobileSearchOpen) keywordInputRef.current?.focus();
  }, [mobileSearchOpen]);

  // Fetch suggestions only while their dropdown can actually show them — not
  // for a query merely hydrated from the URL on page load.
  const { data: keywordSuggestions = [], isFetching: isFetchingSuggestions } = useSuggestions(
    showKeywordDropdown ? debouncedQuery : '',
    locale
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const writeQueryParam = useCallback(
    (value: string) => {
      replaceParams((params) => {
        const trimmed = value.trim();
        // Below the searchable minimum the API would ignore the keyword —
        // never put a no-op q= in the URL (the dropdown hint explains why).
        if (trimmed.length >= MIN_KEYWORD_QUERY_LENGTH) {
          params.set(SEARCH_PARAMS.QUERY, trimmed);
        } else {
          params.delete(SEARCH_PARAMS.QUERY);
        }
      });
    },
    [replaceParams]
  );

  const handleQueryChange = useCallback(
    (value: string) => {
      setLocalQuery(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => writeQueryParam(value), 300);
    },
    [writeQueryParam]
  );

  const closeMobileSearch = useCallback(() => {
    setShowKeywordDropdown(false);
    setMobileSearchOpen(false);
    // Return focus to the chip that opened the overlay.
    mobileChipRef.current?.focus();
  }, []);

  const handleSuggestionSelect = useCallback(
    (item: SuggestionItem) => {
      setShowKeywordDropdown(false);
      // An exact activity pick deep-links to the activity page — suggestions
      // are global while results are scoped to the map area, so a keyword
      // search for it could come back empty.
      if (item.type === 'activity' && item.id) {
        router.push(`/${locale}/activity/${item.id}`);
        return;
      }
      setLocalQuery(item.name);
      clearTimeout(debounceRef.current);
      writeQueryParam(item.name);
      // Picking a suggestion means "done typing": collapse the mobile overlay
      // so the keyboard dismisses and the filtered results are visible.
      if (mobileSearchOpen) closeMobileSearch();
    },
    [writeQueryParam, router, locale, mobileSearchOpen, closeMobileSearch]
  );

  const submitQuery = useCallback(() => {
    clearTimeout(debounceRef.current);
    writeQueryParam(localQuery);
    setShowKeywordDropdown(false);
    // Enter on mobile means "done typing": collapse the overlay (keeping the
    // query) so the keyboard dismisses and the results show underneath.
    if (mobileSearchOpen) closeMobileSearch();
  }, [writeQueryParam, localQuery, mobileSearchOpen, closeMobileSearch]);

  const keywordDropdownOpen = showKeywordDropdown && localQuery.trim().length > 0;
  const showMinLengthHint = localQuery.trim().length > 0 && localQuery.trim().length < MIN_KEYWORD_QUERY_LENGTH;
  // While the hint is up and a refetch is in flight, placeholderData would
  // show the PREVIOUS query's suggestions under the hint — suppress them.
  const suggestionItems = buildSuggestionItems(
    localQuery,
    showMinLengthHint && isFetchingSuggestions ? [] : keywordSuggestions
  );
  const keywordListboxId = useId();
  // Active = a keyword the API actually filters by (sub-minimum never reaches
  // the URL, but guard anyway so the styling can't lie about a no-op filter).
  const hasActiveQuery = currentQuery.trim().length >= MIN_KEYWORD_QUERY_LENGTH;

  const { highlightedIndex, handleKeyDown: handleKeywordKeyDown } = useSuggestionNavigation({
    items: suggestionItems,
    isOpen: keywordDropdownOpen,
    open: () => setShowKeywordDropdown(true),
    close: () => setShowKeywordDropdown(false),
    onPick: handleSuggestionSelect,
    onSubmit: submitQuery,
    resetKey: localQuery
  });

  // The overlay's X is a "cancel search": it clears the active keyword, not
  // just hides the input — otherwise the q= filter keeps silently
  // constraining the results behind a collapsed chip. Collapse-only paths
  // (viewport rotation, Escape) use closeMobileSearch and preserve the query.
  const cancelMobileSearch = useCallback(() => {
    setLocalQuery('');
    clearTimeout(debounceRef.current);
    writeQueryParam('');
    closeMobileSearch();
  }, [writeQueryParam, closeMobileSearch]);

  const handleKeywordInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    // A second Escape (dropdown already closed) collapses the mobile overlay
    // back to the icon chip.
    if (e.key === 'Escape' && !keywordDropdownOpen && mobileSearchOpen) {
      closeMobileSearch();
      return;
    }
    handleKeywordKeyDown(e);
  };

  // Collapse the overlay when the viewport grows past the `sm` breakpoint
  // (e.g. phone rotation): the overlay would be CSS-hidden but still mounted,
  // leaving its portalled popover anchored to an invisible element.
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const mediaQuery = window.matchMedia('(min-width: 640px)');
    const handleChange = () => {
      if (mediaQuery.matches) closeMobileSearch();
    };
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mobileSearchOpen, closeMobileSearch]);

  // Close the mobile overlay when tapping anywhere outside it.
  useEffect(() => {
    if (!mobileSearchOpen) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const overlay = mobileOverlayRef.current;
      const target = e.target as Node;
      // Taps inside the overlay or inside the portalled suggestions popover
      // (which lives outside the overlay subtree) must not close it.
      if (
        overlay &&
        !overlay.contains(target) &&
        !(target instanceof Element && target.closest('[data-radix-popper-content-wrapper]'))
      ) {
        closeMobileSearch();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [mobileSearchOpen, closeMobileSearch]);

  const [displayName] = useSearchParam(SEARCH_PARAMS.DISPLAY_NAME);
  const locationLabel = displayName[0] || t('SearchPage.chooseLocation');

  const [categoryValues, setCategoryValues] = useSearchParam(SEARCH_PARAMS.CATEGORY_ID);
  const [dateValues, setDateValues] = useSearchParam(SEARCH_PARAMS.DATE);
  const [, setDateFrom] = useSearchParam(SEARCH_PARAMS.DATE_FROM);
  const [, setDateTo] = useSearchParam(SEARCH_PARAMS.DATE_TO);
  const [slotTimeValues, setSlotTimeValues] = useSearchParam(SEARCH_PARAMS.SLOT_TIME);
  const [venueValues, setVenueValues] = useSearchParam(SEARCH_PARAMS.IS_AT_HOME);

  const handleDateChange = useCallback(
    (values: string[], customRange?: { from?: Date; to?: Date }) => {
      setDateValues(values);
      if (values.includes('custom') && customRange?.from && customRange?.to) {
        setDateFrom([customRange.from.toISOString()]);
        setDateTo([customRange.to.toISOString()]);
      } else {
        setDateFrom([]);
        setDateTo([]);
      }
    },
    [setDateValues, setDateFrom, setDateTo]
  );

  // Both variants stay in the DOM under CSS breakpoint gating (inline ≥sm /
  // overlay <sm), so each needs its own listbox/option ids, and the portalled
  // suggestions popover — which CSS hiding cannot reach — may only be open for
  // the variant the user is actually interacting with.
  const keywordCombobox = (variant: 'inline' | 'overlay') => {
    const listboxId = `${keywordListboxId}-${variant}`;
    const popoverOpen = keywordDropdownOpen && (mobileSearchOpen ? variant === 'overlay' : variant === 'inline');
    return (
      <Popover open={popoverOpen} onOpenChange={setShowKeywordDropdown}>
        <PopoverAnchor asChild>
          <div
            className={cn(
              // Mirrors the filter chips (px-3 py-2, w-5 h-5 icon) so the
              // input and the chips share the same height.
              'items-center gap-2 rounded-lg border px-3 py-2 min-w-0 transition-colors',
              variant === 'overlay' ? 'flex flex-1' : 'hidden sm:flex sm:flex-none sm:min-w-[180px]',
              hasActiveQuery
                ? 'bg-primary-0 border-primary-200 hover:border-primary-300'
                : 'bg-white border-neutral-200 hover:border-neutral-300'
            )}
          >
            <Search className={cn('w-5 h-5 flex-shrink-0', hasActiveQuery ? 'text-primary-500' : 'text-neutral-500')} />
            <input
              ref={variant === 'overlay' ? keywordInputRef : inlineKeywordInputRef}
              type="text"
              role="combobox"
              aria-expanded={popoverOpen}
              // Only reference the listbox while it exists in the DOM.
              aria-controls={popoverOpen ? listboxId : undefined}
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0 ? suggestionOptionId(listboxId, highlightedIndex) : undefined
              }
              aria-label={t('SearchPage.searchPlaceholder')}
              maxLength={MAX_KEYWORD_QUERY_LENGTH}
              value={localQuery}
              onChange={(e) => {
                handleQueryChange(e.target.value);
                setShowKeywordDropdown(true);
              }}
              onFocus={() => setShowKeywordDropdown(true)}
              onKeyDown={handleKeywordInputKeyDown}
              placeholder={t('SearchPage.searchPlaceholder')}
              // text-base (16px) on mobile prevents iOS Safari from auto-zooming on focus; the
              // dense text-sm is restored at sm+ where the inline variant lives.
              className="bg-transparent outline-none text-base sm:text-sm leading-5 text-neutral-700 w-full min-w-0 placeholder:text-neutral-500"
            />
            {variant === 'overlay' && (
              <button
                type="button"
                aria-label={tCommon('cancel')}
                className="flex items-center justify-center flex-shrink-0 text-neutral-500 hover:text-neutral-700"
                onClick={cancelMobileSearch}
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] max-h-[280px] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <KeywordSuggestionsList
            listboxId={listboxId}
            items={suggestionItems}
            highlightedIndex={highlightedIndex}
            isLoading={isFetchingSuggestions}
            onSelect={handleSuggestionSelect}
            showMinLengthHint={showMinLengthHint}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div
      className={cn(
        // On mobile (< sm): the row fills the full width; the location button stretches
        // while the other (icon-only) filters keep their natural compact width.
        // On sm+: restore the original horizontal-scroll layout with natural widths.
        // `relative` anchors the mobile keyword-search overlay.
        'relative flex flex-row items-center gap-2 sm:gap-3 sm:overflow-x-auto',
        'scrollbar-width-none [&::-webkit-scrollbar]:hidden [&::-ms-overflow-style:none]',
        className
      )}
    >
      <LocationFilter
        isMobile={isMobile}
        locationLabel={locationLabel}
        isActive={displayName.length > 0 ? true : undefined}
        setLocation={setLocation}
        onOpenChange={onLocationPickerOpenChange}
        className="flex-1 min-w-0 sm:flex-none"
      />

      {/* Keyword search.
          ≥sm: inline input, height-matched with the chips.
          <sm: icon-only chip (the row is already dense); tapping it expands
          the input as an overlay over the whole filter row, so no extra
          vertical space is ever used. CSS-gated on the same `sm` breakpoint
          as the row layout — no JS-measured flash on first paint. */}
      {keywordCombobox('inline')}
      <button
        ref={mobileChipRef}
        type="button"
        aria-label={t('SearchPage.searchPlaceholder')}
        onClick={() => setMobileSearchOpen(true)}
        className={cn(
          'flex sm:hidden items-center justify-center px-3 py-2 border rounded-lg transition-colors flex-shrink-0',
          // Closing the overlay restores focus here for keyboard users; the
          // ring must only show for keyboard focus, not the programmatic
          // focus after tapping the X (iOS would draw its default ring).
          'outline-none focus-visible:ring-2 focus-visible:ring-primary-300',
          hasActiveQuery
            ? 'bg-primary-0 border-primary-200 hover:border-primary-300'
            : 'bg-white border-neutral-200 hover:border-neutral-300'
        )}
      >
        <Search className={cn('w-5 h-5', hasActiveQuery ? 'text-primary-500' : 'text-neutral-900')} />
      </button>
      {mobileSearchOpen && (
        <div ref={mobileOverlayRef} className="absolute inset-0 z-20 bg-white flex items-center sm:hidden">
          {keywordCombobox('overlay')}
        </div>
      )}

      {categories && categories.length > 0 && (
        <MultiSelectFilterCompact
          key={SEARCH_PARAMS.CATEGORY_ID}
          label={t('SearchPage.category')}
          icon={<Tag className="w-5 h-5 text-neutral-900" />}
          onChange={setCategoryValues}
          value={categoryValues}
          options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
          showClear={true}
          clearText={t('SearchPage.clear')}
          applyText={tCommon('apply')}
          hideLabelOnMobile
          className="flex-shrink-0"
        />
      )}

      <DateRangeFilter
        label={t('SearchPage.date')}
        icon={<Calendar className="w-5 h-5 text-neutral-900" />}
        options={[
          { value: 'today', label: t('SearchPage.today') },
          { value: 'tomorrow', label: t('SearchPage.tomorrow') },
          { value: 'this-week', label: t('SearchPage.thisWeek') },
          { value: 'this-month', label: t('SearchPage.thisMonth') },
          { value: 'next-30-days', label: t('SearchPage.next30Days') },
          { value: 'custom', label: t('SearchPage.customDuration') }
        ]}
        value={dateValues}
        onChange={handleDateChange}
        showClear={true}
        clearText={t('SearchPage.clear')}
        applyText={tCommon('apply')}
        customDateLabel={t('SearchPage.customDuration')}
        locale={locale}
        translations={{
          cancel: tCommon('cancel'),
          apply: tCommon('apply'),
          maxDaysWarning: t('SearchPage.maxDaysWarning', { maxDays: '90' })
        }}
        hideLabelOnMobile
        className="flex-shrink-0"
      />

      <MultiSelectFilterCompact
        label={t('SearchPage.slotTime')}
        icon={<Clock className="w-5 h-5 text-neutral-900" />}
        onChange={setSlotTimeValues}
        value={slotTimeValues}
        options={[
          { value: 'morning', label: t('SearchPage.morning') },
          { value: 'afternoon', label: t('SearchPage.afternoon') },
          { value: 'evening', label: t('SearchPage.evening') }
        ]}
        showClear={true}
        clearText={t('SearchPage.clear')}
        applyText={tCommon('apply')}
        hideLabelOnMobile
        className="flex-shrink-0"
      />

      <MultiSelectFilterCompact
        label={t('SearchPage.activityVenue')}
        icon={<MapPin className="w-5 h-5 text-neutral-900" />}
        onChange={setVenueValues}
        value={venueValues}
        options={[
          { value: 'true', label: t('SearchPage.filterAtHome') },
          { value: 'false', label: t('SearchPage.filterAtVenue') }
        ]}
        showClear={true}
        clearText={t('SearchPage.clear')}
        applyText={tCommon('apply')}
        hideLabelOnMobile
        className="flex-shrink-0"
      />
    </div>
  );
}
